import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import LeakPlayer from './LeakPlayer';
import './Navigation.css';

const Navigation: React.FC = React.memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang, t } = useI18n();
  const [activeSection, setActiveSection] = useState('hero');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isBeatsPage = location.pathname === '/beats' || location.pathname === '/beats/';

  const handleScroll = useCallback(() => {
    if (isBeatsPage) return;
    const sections = ['hero', 'work', 'about', 'player', 'credits', 'contact'];
    const scrollPosition = window.scrollY + window.innerHeight * 0.3;
    for (const section of sections) {
      const element = document.getElementById(section);
      if (element) {
        const offsetTop = element.offsetTop;
        const offsetHeight = element.offsetHeight;
        if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
          setActiveSection(section);
          break;
        }
      }
    }
  }, [isBeatsPage]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => { handleScroll(); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) setIsMenuOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleNavClick = useCallback((sectionId: string) => {
    if (isBeatsPage) {
      navigate('/');
      setTimeout(() => scrollToSection(sectionId), 100);
    } else {
      scrollToSection(sectionId);
    }
    setIsMenuOpen(false);
  }, [scrollToSection, isBeatsPage, navigate]);

  const navItems = [
    { id: 'work', label: t('nav.work') },
    { id: 'about', label: t('nav.about') },
    { id: 'player', label: t('nav.music') },
    { id: 'contact', label: t('nav.contact') },
  ];

  return (
    <>
      {/* Fixed nav bar */}
      <nav className="navigation">
        <button className="nav-logo" data-text="andrxw" onClick={() => handleNavClick('hero')}>
          <svg className="sigil" viewBox="0 0 60 60" width="24" height="24">
            <ellipse cx="30" cy="30" rx="28" ry="10" fill="none" stroke="white" strokeWidth="1" transform="rotate(-30 30 30)" opacity="0.6"/>
            <path d="M30 10 L32 28 L50 30 L32 32 L30 50 L28 32 L10 30 L28 28 Z" fill="white" opacity="0.9"/>
          </svg>
          andrxw
        </button>

        {/* Desktop links — hidden on mobile via CSS */}
        <ul className="nav-links-desktop">
          {navItems.map(({ id, label }) => (
            <li key={id}>
              <button
                onClick={() => handleNavClick(id)}
                className={`nav-link ${activeSection === id ? 'active' : ''}`}
              >
                [ {label} ]
              </button>
            </li>
          ))}
        </ul>

        <div className="nav-right">
          <LeakPlayer />
          <button
            className="nav-lang"
            onClick={() => setLang(lang === 'en' ? 'ro' : 'en')}
          >
            {lang === 'en' ? 'RO' : 'EN'}
          </button>
          <span className="nav-ver">VER. <span>2.0</span></span>
          <button
            className="nav-toggle"
            onClick={() => setIsMenuOpen(prev => !prev)}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`} />
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`} />
            <span className={`burger-line ${isMenuOpen ? 'open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Fullscreen mobile menu — rendered OUTSIDE nav to avoid clipping */}
      {isMenuOpen && (
        <div className="mobile-menu">
          <button className="mobile-close" onClick={() => setIsMenuOpen(false)} aria-label="Close menu">
            ✕
          </button>
          <ul className="mobile-menu-links">
            {isBeatsPage ? (
              <li>
                <button onClick={() => { navigate('/'); setIsMenuOpen(false); }} className="mobile-link">
                  {t('nav.back')}
                </button>
              </li>
            ) : (
              navItems.map(({ id, label }) => (
                <li key={id}>
                  <button
                    onClick={() => handleNavClick(id)}
                    className={`mobile-link ${activeSection === id ? 'active' : ''}`}
                  >
                    [ {label} ]
                  </button>
                </li>
              ))
            )}
            <li>
              <button
                className="mobile-link lang-toggle"
                onClick={() => { setLang(lang === 'en' ? 'ro' : 'en'); setIsMenuOpen(false); }}
              >
                {lang === 'en' ? '[ ROMÂNĂ ]' : '[ ENGLISH ]'}
              </button>
            </li>
          </ul>
        </div>
      )}
    </>
  );
});

export default Navigation;
